import { createTheme, ThemeOptions } from '@mui/material/styles';

// PolarRide Brand Colors - Professional Blue Theme
const colors = {
  // Blue variations (primary brand color)
  blue: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6', // Main Blue
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
  },
  // Grey variations (for secondary elements)
  grey: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },
  // Light mode colors
  light: {
    primary: '#9A7B4F', // Cognac
    primaryLight: '#D4B483',
    primaryDark: '#6F5432',
    background: '#FDFDFD', // Off-white
    paper: '#FFFFFF',
    text: '#2D363A',
    textSecondary: '#5D737E', // Steel blue
    border: '#DAE7ED',
    hover: '#F0EFEB', // Linen
  },
  // Dark mode colors
  dark: {
    primary: '#D4B483', // Golden leather
    primaryLight: '#E8C299',
    primaryDark: '#9A7B4F',
    background: '#0F0F0F', // Rich black
    paper: '#1A1A1A', // Soft black
    text: '#FFFFFF',
    textSecondary: '#8FADBF', // Light steel
    border: '#374247',
    hover: '#1A1A1A',
  },
  // Status colors - automotive theme
  status: {
    success: '#4A7C59', // Forest green
    warning: '#D39C48', // Amber
    error: '#B74A4A', // Terra cotta
    info: '#5D737E', // Steel blue
  }
};

// Helper function to create theme based on mode
export const createDynamicTheme = (mode: 'light' | 'dark') => {
  const isLight = mode === 'light';
  const palette = isLight ? colors.light : colors.dark;

  const themeOptions: ThemeOptions = {
    palette: {
      mode,
      primary: {
        main: palette.primary,
        light: palette.primaryLight,
        dark: palette.primaryDark,
        contrastText: isLight ? '#FFFFFF' : '#121212',
      },
      secondary: {
        main: colors.grey[600],
        light: colors.grey[400],
        dark: colors.grey[800],
        contrastText: '#ffffff',
      },
      success: {
        main: colors.status.success,
        contrastText: '#FFFFFF',
      },
      warning: {
        main: colors.status.warning,
        contrastText: '#FFFFFF',
      },
      error: {
        main: colors.status.error,
        contrastText: '#FFFFFF',
      },
      info: {
        main: colors.status.info,
        contrastText: '#FFFFFF',
      },
      background: {
        default: palette.background,
        paper: palette.paper,
      },
      text: {
        primary: palette.text,
        secondary: palette.textSecondary,
        disabled: isLight ? '#BDBDBD' : '#757575',
      },
      divider: palette.border,
      action: {
        hover: palette.hover,
        selected: isLight ? '#E3F2FD' : '#1E3A8A',
      },
    },
    typography: {
      fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", "Roboto", sans-serif',
      h1: {
        fontSize: '2.25rem',
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
      },
      h2: {
        fontSize: '1.875rem',
        fontWeight: 600,
        lineHeight: 1.3,
        letterSpacing: '-0.01em',
      },
      h3: {
        fontSize: '1.5rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h4: {
        fontSize: '1.25rem',
        fontWeight: 600,
        lineHeight: 1.4,
      },
      h5: {
        fontSize: '1.125rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: 1.5,
      },
      body1: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: 1.5,
      },
      body2: {
        fontSize: '0.8125rem',
        fontWeight: 400,
        lineHeight: 1.5,
      },
      button: {
        fontSize: '0.875rem',
        fontWeight: 500,
        textTransform: 'none',
        letterSpacing: '0.01em',
      },
    },
    shape: {
      borderRadius: 8,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '8px 16px',
            boxShadow: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: isLight 
                ? '0 2px 8px rgba(0,0,0,0.1)' 
                : '0 2px 8px rgba(255,255,255,0.1)',
            },
          },
          contained: {
            backgroundColor: palette.primary,
            color: isLight ? '#FFFFFF' : '#121212',
            '&:hover': {
              backgroundColor: isLight ? '#1d4ed8' : '#1d4ed8',
            },
          },
          containedSecondary: {
            backgroundColor: colors.grey[700],
            color: '#ffffff',
            '&:hover': {
              backgroundColor: colors.grey[800],
            },
          },
          outlined: {
            borderColor: palette.border,
            color: palette.text,
            '&:hover': {
              borderColor: palette.textSecondary,
              backgroundColor: palette.hover,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: isLight 
              ? '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)'
              : '0 1px 3px rgba(255,255,255,0.05), 0 1px 2px rgba(255,255,255,0.1)',
            border: 'none',
            transition: 'all 0.2s ease',
            backgroundColor: palette.paper,
            '&:hover': {
              boxShadow: isLight 
                ? '0 4px 12px rgba(0,0,0,0.1)'
                : '0 4px 12px rgba(255,255,255,0.1)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontWeight: 500,
            border: 'none',
          },
          filled: {
            backgroundColor: palette.hover,
            color: palette.text,
            '&.MuiChip-colorSecondary': {
              backgroundColor: colors.grey[200],
              color: colors.grey[800],
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 4,
            backgroundColor: palette.hover,
            height: 6,
          },
          bar: {
            borderRadius: 4,
          },
          barColorPrimary: {
            backgroundColor: palette.primary,
          },
          barColorSecondary: {
            backgroundColor: colors.grey[600],
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: palette.hover,
            color: palette.text,
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            fontWeight: 600,
          },
          colorSecondary: {
            backgroundColor: colors.grey[600],
            color: '#ffffff',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: palette.textSecondary,
            '&:hover': {
              backgroundColor: palette.hover,
              color: palette.text,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${palette.border}`,
            color: palette.text,
          },
          head: {
            backgroundColor: palette.hover,
            fontWeight: 600,
            color: palette.textSecondary,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: palette.paper,
            color: palette.text,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: palette.paper,
            color: palette.text,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: palette.paper,
            color: palette.text,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
        },
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'transparent',
              borderRadius: 8,
              '& fieldset': {
                borderColor: palette.border,
                borderWidth: 1,
              },
              '&:hover fieldset': {
                borderColor: palette.textSecondary,
              },
              '&.Mui-focused fieldset': {
                borderColor: palette.primary,
                borderWidth: 2,
              },
            },
            '& .MuiInputLabel-root': {
              color: palette.textSecondary,
              '&.Mui-focused': {
                color: palette.primary,
              },
            },
            '& .MuiInputBase-input': {
              color: palette.text,
              fontSize: '0.875rem',
            },
          },
        },
      },
      // Gold accent only for special elements
      MuiRating: {
        styleOverrides: {
          iconFilled: {
            color: colors.blue[500],
          },
        },
      },
    },
  };

  return createTheme(themeOptions);
};

// Helper function for status colors
export const getStatusColor = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    active: colors.status.success,
    inactive: '#9E9E9E',
    pending: colors.status.warning,
    suspended: colors.status.error,
    rented: '#60a5fa',
    available: colors.status.success,
    verified: colors.status.success,
  };
  return statusMap[status.toLowerCase()] || '#9E9E9E';
};

// Helper function to determine if it's day or night
export const getTimeBasedTheme = (): 'light' | 'dark' => {
  const hour = new Date().getHours();
  // Dark mode from 6 PM (18:00) to 6 AM (6:00)
  return (hour >= 18 || hour < 6) ? 'dark' : 'light';
};